<template>
  <div>
    <div v-if="administratorDataIsLoading" class="d-flex justify-content-center m-5">
      <div class="spinner-border text-primary">
        <span class="sr-only">{{$t('state.loading')}}</span>
      </div>
    </div>
    <b-card v-if="!administratorDataIsLoading">
      <b-card-body>
        <div class="headline d-flex flex-row align-items-center w-100 ml-1 pb-3">
          <h2 class="card-title font-weight-bolder">{{ $t('administrator.details.security-groups.title') }}</h2>
        </div>
        <div class="col-12">
          <div class="row">
            <div v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_GET'" id="availableGroups" class="col-5">
              <b-card
                  border-variant="primary"
                  header-bg-variant="success"
                  header-text-variant="white"
                  align="left"
              >
                <template #header>
                  <div class="d-flex align-items-center justify-content-between">
                    <p class="mb-0">{{ availableGroupsHeader }}</p>
                    <button class="btn btn-primary btn-xs"
                            @click="toggleAll('availableGroups')">{{ $t('administrator.edit-group.toggle-all') }}</button>
                  </div>
                </template>
                <div class="d-flex">
                  <b-form-group
                      label-for="group-key"
                      class="mr-1"
                  >
                    <b-form-input id="group-nam1e" :placeholder="$t('administrator.edit-group.find-group')" v-model="filterGroupsPhrase" trim></b-form-input>
                  </b-form-group>
                </div>
                <draggable v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_GET'" class="create-group-modal__groups-list" v-model="filteredGroups" group="people">
                  <div
                      v-for="group in filteredGroups"
                      :key="group.name"
                  >
                    <b-form-checkbox
                        :id="group.name"
                        :name="group.name"
                        :value="group.name"
                    >
                      <div class="d-flex create-group-modal__role-text" :title="group.name">
                        <div class="font-weight-normal">{{ group.name }}</div>
                      </div>
                    </b-form-checkbox>
                  </div>
                </draggable>
              </b-card>
            </div>
            <div  class="col-2 d-flex flex-column justify-content-center align-items-center">
              <b-button @click="moveToSelectedGroups" class="w-100 mb-2 create-group-modal__operation-button" variant="success">
                {{ $t('administrator.edit-group.btn.add') }}
                <i class="fas fa-arrow-right ml-1"></i>
              </b-button>
              <b-button @click="moveToAvailableGroups" class="w-100 create-group-modal__operation-button" variant="danger">
                <i class="fas fa-arrow-left mr-1"></i>
                {{ $t('administrator.edit-group.btn.remove') }}
              </b-button>
            </div>
            <div v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_POST'" id="selectedGroups" class="col-5">
              <b-card
                  border-variant="primary"
                  header-bg-variant="success"
                  header-text-variant="white"
                  align="left"
              >
                <template #header>
                  <div class="d-flex align-items-center justify-content-between">
                    <p class="mb-0">{{ selectedGroupsHeader }}</p>
                    <button class="btn btn-primary btn-xs" @click="toggleAll('selectedGroups')">{{ $t('administrator.edit-group.toggle-all') }}</button>
                  </div>
                </template>
                <div class="d-flex">
                  <b-form-group
                      label-for="group-key"
                      class="mr-1"
                  >
                    <b-form-input id="group-nam1e" :placeholder="$t('administrator.edit-group.find-group')" v-model="filterSelectedGroupsPhrase" trim></b-form-input>
                  </b-form-group>
                </div>
                <draggable class="create-group-modal__groups-list" v-model="filteredSelectedGroups" group="people">
                  <div
                      v-for="group in filteredSelectedGroups"
                      :key="group.name"
                  >
                    <b-form-checkbox
                        :id="group.name"
                        :name="group.name"
                        :value="group.name"
                    >
                      <div class="d-flex create-group-modal__role-text" :title="group.name">
                        <div class="font-weight-normal">{{ group.name }}</div>
                      </div>
                    </b-form-checkbox>
                  </div>
                </draggable>
              </b-card>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import draggable from 'vuedraggable';

export default {
  components: { draggable },
  data() {
    return {
      filterGroupsPhrase: '',
      filterSelectedGroupsPhrase: '',
      availableGroupsChecked: [],
      selectedGroupsChecked: []
    }
  },
  mounted() {
    if(this.groups) {
      this.setGroups();
    }
  },
  computed: {
    ...mapState({
      administratorDataIsLoading: state => state.administrator.administratorDataIsLoading,
      administratorData: state => state.administrator.administratorDetails,
      groups: state => state.administrator.groups,
      storeAvailableGroups: state => state.administrator.availableGroups,
      storeSelectedGroups: state => state.administrator.selectedGroups
    }),
    filteredGroups: {
      get() {
        let filteredGroups;

        if(this.$store.state.administrator.availableGroups) {
          let allGroups = this.$store.state.administrator.availableGroups;
          filteredGroups = allGroups.filter(group => group.name.toLowerCase().match(this.filterGroupsPhrase.toLowerCase()));
        }

        return filteredGroups;
      },
      set(value) {
        this.$store.commit('administrator/setAvailableGroups', value);
      }
    },
    filteredSelectedGroups: {
      get() {
        let filteredGroups;

      if (this.$store.state.administrator.selectedGroups) {
        let allGroups = this.$store.state.administrator.selectedGroups;
        filteredGroups = allGroups.filter(group => group.name.toLowerCase().match(this.filterSelectedGroupsPhrase.toLowerCase()));
        }

        return filteredGroups;
      },
      set(value) {
        this.$store.commit('administrator/setSelectedGroups', value);
      }
    },
    availableGroupsHeader() {
      let header;

      if(this.storeAvailableGroups) {
        header = `${this.$t('administrator.edit-group.available_groups')} (${this.storeAvailableGroups.length})`;
      }

      return header;
    },
    selectedGroupsHeader() {
      let header;

      if(this.storeSelectedGroups) {
        header = `${this.$t('administrator.edit-group.selected_groups')} (${this.storeSelectedGroups.length})`;
      }

      return header;
    }
  },
  methods: {
    setGroups() {
      if(this.administratorData) {
        const groups = [];

        this.groups.forEach(group => {
          this.administratorData.groups.forEach(administratorGroup => {
            if(group.name === administratorGroup.name) {
              groups.push(group);
            }
          })
        });

        const availableGroups = this.groups.filter(item => !groups.includes(item));

        this.$store.commit('administrator/setAvailableGroups', availableGroups);

        this.selectedGroups = groups;
        this.$store.commit('administrator/setSelectedGroups', groups);
      } else {
        this.$store.commit('administrator/setAvailableGroups', this.groups);
      }
    },
    moveToSelectedGroups() {
      const checkedCheckboxes = document.querySelectorAll('#availableGroups input:checked');

      checkedCheckboxes.forEach(checkbox => {
        const findCheckedGroups = this.storeAvailableGroups.find(group => group.name === checkbox.value);
        const selectedGroups = [...this.storeSelectedGroups];
        const availableGroups = this.storeAvailableGroups.filter(group => group.name !== checkbox.value);

        selectedGroups.push(findCheckedGroups);

        this.$store.commit('administrator/setSelectedGroups', selectedGroups);
        this.$store.commit('administrator/setAvailableGroups', availableGroups);
      });
    },
    moveToAvailableGroups() {
      const checkedCheckboxes = document.querySelectorAll('#selectedGroups input:checked');
      checkedCheckboxes.forEach(checkbox => {
        const findCheckedGroups = this.storeSelectedGroups.find(group => group.name === checkbox.value);

        const availableGroups = [...this.storeAvailableGroups];
        availableGroups.unshift(findCheckedGroups);

        const selectedGroups = this.storeSelectedGroups.filter(group => group.name !== checkbox.value);

        this.$store.commit('administrator/setSelectedGroups', selectedGroups);
        this.$store.commit('administrator/setAvailableGroups', availableGroups);
      });
    },
    toggleAll(id) {
      const checkboxes = document.querySelectorAll(`#${id} input`);

      checkboxes.forEach(checkbox => {
        checkbox.checked = !checkbox.checked;
      });
    }
  },
  watch: {
    groups() {
      this.setGroups();
    },
    administratorData() {
      this.setGroups();
    }
  }
}
</script>
